//src/services/materials2050Service.js
const PROXY_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "/api/2050materials"
    : process.env.REACT_APP_2050_PROXY_TARGET || "https://embodix.vercel.app/api/2050materials";

const RESOURCE_CACHE = new Map();

function toCacheKey(path) {
  return path;
}

function appendQuery(url, key, value) {
  if (value === undefined || value === null || value === "") return url;
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
}

function appendMulti(url, key, values) {
  if (!Array.isArray(values) || values.length === 0) return url;
  let out = url;
  values.forEach((v) => {
    if (v === undefined || v === null || v === "") return;
    out = appendQuery(out, key, v);
  });
  return out;
}

async function fetchJSON(path, { signal } = {}) {
  const cacheKey = toCacheKey(path);
  if (!signal && RESOURCE_CACHE.has(cacheKey)) return RESOURCE_CACHE.get(cacheKey);

  const url = `${PROXY_BASE_URL}?path=${encodeURIComponent(path)}`;

  const maxRetries = 2;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (signal?.aborted) {
      const abortErr = new Error("Aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    const response = await fetch(url, { headers: { Accept: "application/json" }, signal });

    if (response.ok) {
      const data = await response.json();
      if (!signal) RESOURCE_CACHE.set(cacheKey, data);
      return data;
    }

    const status = response.status;
    const retryAfterHeader = response.headers.get("Retry-After");

    if (status === 429 && attempt < maxRetries) {
      const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;
      const waitMs = Number.isFinite(retryAfterSeconds)
        ? Math.max(500, retryAfterSeconds * 1000)
        : 800 * (attempt + 1);

      await new Promise((r) => setTimeout(r, waitMs));
      continue;
    }

    let details = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text();
    }

    let detailsText = "";
    if (typeof details === "string") detailsText = details;
    else if (details && typeof details === "object") detailsText = JSON.stringify(details);

    const err = new Error(`2050 Materials request failed (${status}) ${detailsText}`.trim());
    err.status = status;
    err.retryAfter = retryAfterHeader;
    throw err;
  }
}

function decodeHtmlEntities(input) {
  const s = String(input ?? "");
  if (!s) return s;

  if (typeof window !== "undefined" && window?.document?.createElement) {
    const el = document.createElement("textarea");
    el.innerHTML = s;
    return el.value;
  }

  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function normalizeProductsResponse(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.results)) return raw.results;
  if (raw && Array.isArray(raw.products)) return raw.products;
  return [];
}

const FILTER_KEY_ALIASES = {
  product_types: "product_type",
  material_type: "material_types",
  country: "manufacturing_country",
  certificate_types: "certificate_type"
};

function parseNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function pickFirstNumeric(obj) {
  if (!obj || typeof obj !== "object") return null;
  for (const v of Object.values(obj)) {
    const n = parseNumber(v);
    if (n !== null) return n;
  }
  return null;
}

function extractGwpFromManufacturing(manufacturing) {
  const direct = parseNumber(manufacturing);
  if (direct !== null) return direct;

  if (manufacturing && typeof manufacturing === "object") {
    const keys = [
      "global_warming_potential",
      "global_warming_potential_total",
      "gwp",
      "gwp_total",
      "total",
      "a1a2a3",
      "A1A2A3"
    ];
    for (const k of keys) {
      const n = parseNumber(manufacturing[k]);
      if (n !== null) return n;
    }
    return pickFirstNumeric(manufacturing);
  }

  return null;
}

function prettifyBaseKey(base) {
  return String(base || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function moduleFromSuffix(suffix) {
  const s = String(suffix || "").trim();
  if (!s) return null;
  if (s === "A1A2A3") return "A1-A3";
  if (s === "A1A2A3A4") return "A1-A4";
  return s;
}

function extractBreakdownImpactsFromFullProduct(product) {
  if (!product || typeof product !== "object") return [];
  const groups = new Map();

  Object.entries(product).forEach(([key, rawValue]) => {
    if (!key.includes("__")) return;
    const n = parseNumber(rawValue);
    if (n === null) return;

    const parts = key.split("__");
    if (parts.length !== 2) return;

    const base = parts[0];
    const suffix = parts[1];
    const moduleKey = moduleFromSuffix(suffix);
    if (!moduleKey) return;

    if (!groups.has(base)) groups.set(base, {});
    groups.get(base)[moduleKey] = n;
  });

  const impacts = [];
  groups.forEach((modules, base) => {
    const total = Object.values(modules).reduce((a, b) => a + (parseNumber(b) || 0), 0);
    impacts.push({
      id: base,
      description: prettifyBaseKey(base),
      unit: /global_warming_potential/i.test(base) ? "kg CO2e" : null,
      modules,
      total
    });
  });

  impacts.sort((a, b) => {
    if (a.id === "global_warming_potential_total") return -1;
    if (b.id === "global_warming_potential_total") return 1;
    if (a.id === "global_warming_potential_fossil") return -1;
    if (b.id === "global_warming_potential_fossil") return 1;
    return a.description.localeCompare(b.description);
  });

  return impacts;
}

export async function fetchProductFilters({ signal, mini = true, filters = {} } = {}) {
  let path = "developer/api/get_product_filters";
  if (mini) path = appendQuery(path, "data", "mini");

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) path = appendMulti(path, key, value);
    else path = appendQuery(path, key, value);
  });

  return fetchJSON(path, { signal });
}

function normalizePagedProductsResponse(raw) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const results = Array.isArray(raw.results) ? raw.results : normalizeProductsResponse(raw);

    const totalCount = Number(raw.TotalProducts ?? raw.total ?? raw.count ?? raw.total_count ?? results.length);
    const currentPage = Number(raw.current_page ?? raw.page ?? 1);
    const pageSize = Number(raw.countProductsOnPage ?? raw.page_size ?? results.length);

    return {
      results,
      totalCount: Number.isFinite(totalCount) ? totalCount : results.length,
      currentPage: Number.isFinite(currentPage) ? currentPage : 1,
      pageSize: Number.isFinite(pageSize) ? pageSize : results.length,
      next: raw.next ?? null,
      previous: raw.previous ?? null
    };
  }

  const arr = normalizeProductsResponse(raw);
  return { results: arr, totalCount: arr.length, currentPage: 1, pageSize: arr.length, next: null, previous: null };
}

export async function fetchProductsPageByUrl(url, { signal } = {}) {
  if (!url) throw new Error("Missing page url");
  const raw = await fetchJSON(String(url), { signal });
  const meta = normalizePagedProductsResponse(raw);

  return {
    data: meta.results,
    totalCount: meta.totalCount,
    currentPage: meta.currentPage,
    pageSize: meta.pageSize,
    next: meta.next,
    previous: meta.previous
  };
}

export async function searchProductsOpen(query, { signal, page = 1, filters = {} } = {}) {
  let path = "developer/api/get_products_open_api";

  if (query && String(query).trim()) {
    path = appendQuery(path, "name", String(query).trim());
  }

  path = appendQuery(path, "page", page);

  Object.entries(filters || {}).forEach(([key, value]) => {
    const apiKey = FILTER_KEY_ALIASES[key] || key;
    if (Array.isArray(value)) path = appendMulti(path, apiKey, value);
    else path = appendQuery(path, apiKey, value);
  });

  const raw = await fetchJSON(path, { signal });
  const meta = normalizePagedProductsResponse(raw);

  return {
    data: meta.results,
    totalCount: meta.totalCount,
    currentPage: meta.currentPage,
    pageSize: meta.pageSize,
    next: meta.next,
    previous: meta.previous
  };
}

export async function loadProductDetailsOpen(unique_product_uuid_v2, { signal } = {}) {
  let path = "developer/api/get_products_open_api";
  path = appendQuery(path, "unique_product_uuid_v2", unique_product_uuid_v2);
  const raw = await fetchJSON(path, { signal });
  const items = normalizeProductsResponse(raw);
  return items[0] || null;
}

export async function searchProductsFull(query, { signal, page = 1, filters = {} } = {}) {
  let path = "developer/api/get_products";

  if (query && String(query).trim()) {
    path = appendQuery(path, "name", String(query).trim());
  }

  path = appendQuery(path, "page", page);

  Object.entries(filters || {}).forEach(([key, value]) => {
    const apiKey = FILTER_KEY_ALIASES[key] || key;
    if (Array.isArray(value)) path = appendMulti(path, apiKey, value);
    else path = appendQuery(path, apiKey, value);
  });

  const raw = await fetchJSON(path, { signal });
  const meta = normalizePagedProductsResponse(raw);

  return {
    data: meta.results,
    totalCount: meta.totalCount,
    currentPage: meta.currentPage,
    pageSize: meta.pageSize,
    next: meta.next,
    previous: meta.previous
  };
}

export async function loadProductDetailsFull(unique_product_uuid_v2, { signal } = {}) {
  let path = "developer/api/get_products";
  path = appendQuery(path, "unique_product_uuid_v2", unique_product_uuid_v2);
  const raw = await fetchJSON(path, { signal });
  const items = normalizeProductsResponse(raw);
  return items[0] || null;
}

export function normalize2050ListItem(product) {
  const uuid = product?.unique_product_uuid_v2 || product?.id || product?.uuid || null;
  const title = decodeHtmlEntities(product?.name || "Unnamed product");
  const company = decodeHtmlEntities(product?.company_name || product?.company || "");

  return {
    uuid,
    displayName: title,
    fallbackName: title,
    company: company || null,
    updated: product?.updated || null,
    created: product?.created || null,
    product_url: product?.product_url || null,
    certificate_url: product?.certificate_url || null,
    epd_operator: product?.epd_operator || null,
    raw: product
  };
}

export function normalize2050Detail(product, { mode = "open" } = {}) {
  const uuid = product?.unique_product_uuid_v2 || product?.id || product?.uuid || null;
  const name = decodeHtmlEntities(product?.name || "Unnamed product");
  const manufacturer = decodeHtmlEntities(product?.company_name || product?.company || "");

  const declaredUnit =
    product?.material_facts?.declared_unit ||
    product?.material_facts?.declaredUnit ||
    product?.declared_unit ||
    product?.declaredUnit ||
    null;

  let impacts = [];
  if (mode === "full") {
    impacts = extractBreakdownImpactsFromFullProduct(product);
  } else {
    const manufacturing = product?.material_facts?.manufacturing;
    const gwp = extractGwpFromManufacturing(manufacturing);
    impacts = [
      {
        id: "gwp_manufacturing",
        description: "Global warming potential",
        unit: "kg CO2e",
        modules: { "A1-A3": gwp === null ? 0 : gwp },
        total: gwp === null ? 0 : gwp
      }
    ];
  }

  const geography =
    product?.manufacturing_location ||
    product?.manufacturing_country ||
    product?.country ||
    product?.city ||
    null;

  return {
    uuid,
    name,
    manufacturer: manufacturer || null,
    declaredUnit: declaredUnit ? { unit: String(declaredUnit), amount: 1 } : null,
    impacts,
    geography,
    product_url: product?.product_url || null,
    certificate_url: product?.certificate_url || null,
    updated: product?.updated || null,
    created: product?.created || null,
    raw: product
  };
}

export async function test2050Connection({ signal } = {}) {
  return fetchProductFilters({ signal, mini: true });
}
